import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { XIcon, PencilIcon, CircleNotchIcon } from '@phosphor-icons/react';
import {
  useUpdateShareableLink,
  ShareableLink,
} from 'src/api/core/useShareableLinks';
import { useQueryClient } from '@tanstack/react-query';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';

type ReportLineType = 'estimates' | 'predictions' | 'reported';

const LINE_TYPE_OPTIONS: {
  id: ReportLineType;
  label: string;
  value: ReportLineType;
}[] = [
  { id: 'reported', label: 'Reported', value: 'reported' },
  { id: 'estimates', label: 'Estimates', value: 'estimates' },
  { id: 'predictions', label: 'Predictions', value: 'predictions' },
];

interface EditShareableLinkModalProps {
  open: boolean;
  onClose: () => void;
  link: ShareableLink;
}

export function EditShareableLinkModal({
  open,
  onClose,
  link,
}: EditShareableLinkModalProps) {
  const queryClient = useQueryClient();
  const updateLink = useUpdateShareableLink(link.id);
  const currentYear = new Date().getFullYear();

  // Extract reportId from resourceType (e.g., "report:division-rebate-summary" -> "division-rebate-summary")
  const reportId = link.resourceType.startsWith('report:')
    ? link.resourceType.replace('report:', '')
    : null;

  // Form state
  const [linkName, setLinkName] = useState(link.name || '');
  const [emails, setEmails] = useState<string[]>(link.allowedEmails);
  const [emailInput, setEmailInput] = useState('');

  // Filter state
  const [selectedYear, setSelectedYear] = useState<number>(
    (link.metadata?.year as number) || currentYear,
  );
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedLineType, setSelectedLineType] = useState<ReportLineType>(
    (link.metadata?.lineType as ReportLineType) || 'reported',
  );

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch available years
  const { data: availableYearsData } = useRebateAgreementYears();

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options
  const yearOptions = useMemo(() => {
    const years =
      availableYearsData && 'years' in availableYearsData
        ? availableYearsData.years
        : [];
    // Ensure current year is always available
    const allYears = [...new Set([currentYear, ...years])].sort(
      (a, b) => b - a,
    );
    return allYears.map((year) => ({
      id: year.toString(),
      label: year.toString(),
      value: year,
    }));
  }, [availableYearsData, currentYear]);

  // Determine which filters to show based on report type
  const showYearFilter = !!reportId;
  const showDivisionFilter = !!reportId;
  const showLineTypeFilter =
    reportId === 'division-rebate-summary' ||
    reportId === 'supplier-tag-summary';

  // Initialize selected divisions from link metadata when divisions are loaded
  useEffect(() => {
    if (divisionOptions.length > 0 && link.metadata?.divisionIds) {
      const divisionIds = link.metadata.divisionIds as string[];
      const selected = divisionOptions.filter((opt) =>
        divisionIds.includes(opt.id),
      );
      setSelectedDivisions(selected);
    }
  }, [divisionOptions, link.metadata?.divisionIds]);

  // Reset form when link changes
  useEffect(() => {
    setLinkName(link.name || '');
    setEmails(link.allowedEmails);
    setEmailInput('');
    setSelectedYear((link.metadata?.year as number) || currentYear);
    setSelectedLineType(
      (link.metadata?.lineType as ReportLineType) || 'reported',
    );
    // selectedDivisions is handled by the other useEffect when divisionOptions are ready
  }, [link, currentYear]);

  function handleAddEmail() {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (
      trimmedEmail &&
      isValidEmail(trimmedEmail) &&
      !emails.includes(trimmedEmail)
    ) {
      setEmails([...emails, trimmedEmail]);
      setEmailInput('');
    }
  }

  function handleRemoveEmail(emailToRemove: string) {
    setEmails(emails.filter((email) => email !== emailToRemove));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  }

  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async function handleSave() {
    // Build metadata based on selected filters
    const metadata: Record<string, unknown> = {
      year: selectedYear,
    };

    if (selectedDivisions.length > 0) {
      metadata.divisionIds = selectedDivisions.map((d) => d.id);
    }

    if (showLineTypeFilter) {
      metadata.lineType = selectedLineType;
    }

    await updateLink.mutateAsync({
      name: linkName.trim() || null,
      allowedEmails: emails,
      metadata,
    });

    // Invalidate the token query to refresh the page
    queryClient.invalidateQueries({
      queryKey: ['shareable-link-by-token', link.token],
    });

    // Also invalidate the links list
    queryClient.invalidateQueries({
      queryKey: ['shareable-links'],
    });

    onClose();
  }

  function handleClose() {
    // Reset to original values
    setLinkName(link.name || '');
    setEmails(link.allowedEmails);
    setEmailInput('');
    setSelectedYear((link.metadata?.year as number) || currentYear);
    setSelectedLineType(
      (link.metadata?.lineType as ReportLineType) || 'reported',
    );
    onClose();
  }

  const hasChanges =
    linkName.trim() !== (link.name || '') ||
    JSON.stringify(emails.sort()) !==
      JSON.stringify([...link.allowedEmails].sort()) ||
    selectedYear !== ((link.metadata?.year as number) || currentYear) ||
    selectedLineType !==
      ((link.metadata?.lineType as ReportLineType) || 'reported') ||
    JSON.stringify(selectedDivisions.map((d) => d.id).sort()) !==
      JSON.stringify(((link.metadata?.divisionIds as string[]) || []).sort());

  const canSave = emails.length > 0 && hasChanges;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PencilIcon className="h-5 w-5" />
            Edit Shared Link
          </DialogTitle>
          <DialogDescription>
            Update the name, recipients, or filters for this shared link.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Link Name */}
          <div className="space-y-2">
            <Label htmlFor="linkName">Link Name (optional)</Label>
            <Input
              id="linkName"
              placeholder="e.g., Q4 2024 Rebate Summary for Acme Corp"
              value={linkName}
              onChange={(e) => setLinkName(e.target.value)}
            />
          </div>

          {/* Email Section */}
          <div className="space-y-2">
            <Label htmlFor="email">Shared with (email addresses)</Label>
            <div className="flex gap-2">
              <Input
                id="email"
                type="email"
                placeholder="Add email address"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddEmail}
                disabled={
                  !emailInput.trim() || !isValidEmail(emailInput.trim())
                }
              >
                Add
              </Button>
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {emails.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-1 rounded-full hover:bg-muted p-0.5"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {emails.length === 0 && (
              <p className="text-sm text-destructive">
                At least one email address is required.
              </p>
            )}
          </div>

          {/* Filters Section */}
          {reportId && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 block">
                Report Filters
              </Label>
              <div className="space-y-3">
                {showYearFilter && (
                  <SingleSelect
                    label="Year"
                    options={yearOptions}
                    value={
                      yearOptions.find((opt) => opt.value === selectedYear) ||
                      null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedYear(option.value);
                      }
                    }}
                    placeholder="Select year..."
                    searchPlaceholder="Search years..."
                  />
                )}

                {showDivisionFilter && (
                  <MultiSelect
                    label="Divisions"
                    options={divisionOptions}
                    value={selectedDivisions}
                    onChange={setSelectedDivisions}
                    placeholder="All divisions (no filter)"
                    searchPlaceholder="Search divisions..."
                    disabled={divisionsLoading}
                  />
                )}

                {showLineTypeFilter && (
                  <SingleSelect
                    label="Line Type"
                    options={LINE_TYPE_OPTIONS}
                    value={
                      LINE_TYPE_OPTIONS.find(
                        (opt) => opt.value === selectedLineType,
                      ) || null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedLineType(option.value);
                      }
                    }}
                    placeholder="Select line type..."
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateLink.isPending || !canSave}
          >
            {updateLink.isPending && (
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
